<template>
  <v-card class="mb-3 ordera-box-shadow" outlined>
    <!-- Header: ID & Status -->
    <v-list-item>
      <v-list-item-content>
        <v-list-item-subtitle class="text-caption grey--text">
          ID:
          {{ item.orders_guest && item.orders_guest[0] ? item.orders_guest[0].id : "" }}
        </v-list-item-subtitle>
        <v-list-item-title
          class="font-weight-bold user-name-link"
          @click="$emit('show-user-details', item)"
        >
          {{ toLatin(item.name) }} {{ toLatin(item.lastname) }}
        </v-list-item-title>
        <v-list-item-subtitle>
          <v-icon x-small class="mr-1">mdi-phone</v-icon>
          {{ item.phone }}
        </v-list-item-subtitle>
        <v-list-item-subtitle
          v-if="
            isAdmin &&
            item.orders_guest &&
            item.orders_guest[0] &&
            item.orders_guest[0].restaurant
          "
          class="orange--text restaurant-link"
          @click="$emit('show-restaurant-details', item.orders_guest[0].restaurant)"
        >
          <v-icon x-small color="orange" class="mr-1">mdi-store</v-icon>
          {{ item.orders_guest[0].restaurant.name }}
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action>
        <v-menu v-if="item.orders_guest && item.orders_guest[0]" offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              small
              :style="statusChipStyle(item.orders_guest[0].state)"
              v-bind="attrs"
              v-on="on"
              class="cursor-pointer status-chip"
              :loading="changingStatusOrderId === item.orders_guest[0].id"
            >
              <span
                class="status-dot mr-1"
                :style="statusDotStyle(item.orders_guest[0].state)"
              ></span>
              {{ $t(item.orders_guest[0].state) }}
            </v-chip>
          </template>
          <v-list dense>
            <v-list-item
              v-for="status in orderStatuses"
              :key="status.value"
              @click="requestStatusChange(item.orders_guest[0], item, status.value)"
              :disabled="item.orders_guest[0].state === status.value"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small :style="{ color: getStatusColors(status.value).fg }">
                  {{ status.icon }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t(status.value) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-list-item-action>
    </v-list-item>

    <v-divider />

    <!-- Address & Time -->
    <v-list-item>
      <v-list-item-content>
        <v-list-item-subtitle class="text-caption grey--text">
          {{ $t("address") }}
        </v-list-item-subtitle>
        <v-list-item-title v-if="item.orders_guest && item.orders_guest[0]">
          <div v-if="item.orders_guest[0].delivery_type === 0">
            <v-icon small class="mr-1">mdi-motorbike</v-icon>
            {{ $t("delivery_to_address") }}
            <div v-if="item.orders_guest[0].location" class="text-caption mt-1">
              {{ item.orders_guest[0].location.address }}
            </div>
          </div>
          <div v-else-if="item.orders_guest[0].delivery_type === 1">
            <v-icon small class="mr-1">mdi-silverware-fork-knife</v-icon>
            {{ $t("pickup_in_restaurant") }}
          </div>
          <div v-else-if="item.orders_guest[0].delivery_type === 2">
            <v-icon small class="mr-1">mdi-home</v-icon>
            {{ $t("consumption_in_restaurant") }}
          </div>
        </v-list-item-title>
      </v-list-item-content>
      <v-list-item-action v-if="item.orders_guest && item.orders_guest[0]">
        <div class="text-caption grey--text">{{ $t("time") }}</div>
        <div class="font-weight-medium">
          {{ formatOrderTime(item.orders_guest[0]) }}
        </div>
        <div v-if="isAsapOrder(item.orders_guest[0])" class="asap-label primary--text">
          {{ $t("asap") }}
        </div>
      </v-list-item-action>
    </v-list-item>

    <v-divider></v-divider>

    <!-- Orders list -->
    <v-list-item>
      <v-list-item-content>
        <v-list-item-subtitle class="text-caption grey--text mb-2">
          {{ $t("ordered") }}
        </v-list-item-subtitle>
        <div v-if="item.orders_guest && item.orders_guest.length">
          <div v-for="(order, idx) in item.orders_guest" :key="idx">
            <div
              v-for="(transaction, tidx) in order.order_guest_transactions"
              :key="tidx"
              class="d-flex align-center order-item-row"
            >
              <span class="qty-label">{{ transaction.quantity }}×</span>
              <span class="flex-grow-1">
                {{ transaction.food ? transaction.food.name : "" }}
              </span>
              <span class="font-weight-medium" style="white-space: nowrap">
                {{ (transaction.price * transaction.quantity).toFixed(1) }}
                {{ orderCurrency(order) }}
              </span>
            </div>
          </div>
        </div>
      </v-list-item-content>
    </v-list-item>

    <v-divider></v-divider>

    <!-- Total & Details button -->
    <v-card-actions class="justify-space-between">
      <v-btn
        text
        color="orange"
        small
        @click="
          $emit('show-details', { order: item.orders_guest[0], guest: item })
        "
      >
        <v-icon small left>mdi-clipboard-text</v-icon>
        {{ $t("order_details") }}
      </v-btn>
      <div class="text-h6 font-weight-bold primary--text">
        {{ calculateTotal(item).toFixed(1) }} {{ itemCurrency(item) }}
      </div>
    </v-card-actions>

    <prep-time-dialog
      v-model="prepTimeDialogOpen"
      @select-minutes="confirmPrepMinutes"
      @ready-now="confirmReadyNow"
    />
  </v-card>
</template>

<script>
import hasGuestOrderStatus from "@/mixins/report/hasGuestOrderStatus";
import PrepTimeDialog from "@/components/guest/PrepTimeDialog.vue";

export default {
  name: "ReportGuestOrderCardMobile",
  components: { PrepTimeDialog },
  mixins: [hasGuestOrderStatus],
  props: {
    item: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      default: "",
    },
    changingStatusOrderId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    isAdmin() {
      return this.$store.state.user.user?.type === "admin";
    },
  },
  methods: {
    // Currency is per order's restaurant, not the logged-in restaurant.
    orderCurrency(order) {
      return order?.restaurant?.settings?.price || this.currency || this.restaurantCurrency;
    },
    itemCurrency(item) {
      return this.orderCurrency(item?.orders_guest?.[0]);
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.restaurant-link {
  cursor: pointer;
}

.restaurant-link:hover {
  text-decoration-style: solid;
}

.user-name-link {
  cursor: pointer;
  color: inherit;
}

.user-name-link:hover {
  color: #fb6d3b;
  text-decoration: underline;
}

.status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.status-chip {
  font-weight: 600;
  transition: transform 0.15s ease;
}

.status-chip:hover {
  transform: translateY(-1px);
}

.asap-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-top: 2px;
}

.qty-label {
  display: inline-block;
  text-align: right;
  color: rgba(0, 0, 0, 0.64);
  font-weight: 700;
  margin-right: 2px;
}

.order-item-row {
  font-size: 13px;
  margin-bottom: 3px;
}
</style>
